import { Button } from '@repo/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/dropdownMenu'
import { useT } from '@repo/ui/hooks'
import { cn } from '@repo/ui/utils'
import { FileDownIcon, MoreVertical, Pencil, Users } from 'lucide-react'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { useBeneficiaryListAsSheet } from '@/components/beneficiary/list/sheet/hook'
import { downloadCSV } from '@/components/helpers'
import { buildSearchParam, SearchParamsKeyNames, SearchParamsViewNames } from '@/routes'
import { InsightWidget } from '@/store/beneficiary-insights/types'

import { Card } from '../cards/Card'

export function ShowModeCard({
  widget,
  colSpanCss,
  rowSpanCss,
}: {
  widget: InsightWidget
  colSpanCss: string
  rowSpanCss: string
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.dashboard' })
  const { setOpen: setOpenBeneficiaryListAsSheet } = useBeneficiaryListAsSheet()

  function handleDownloadCSV() {
    if (widget.type !== 'chart') return
    void downloadCSV('insights.csv', widget.csv)
  }

  function handleShowBeneficiaries() {
    setOpenBeneficiaryListAsSheet(true, { filterTemplate: { name: '', filters: widget.filters } })
  }

  const canDownloadCSV = useMemo(() => {
    return widget.type === 'chart' && widget.csv && widget.csv.length > 0
  }, [widget])

  const canShowBeneficiaries = useMemo(() => {
    return widget.filters.length > 0
  }, [widget])

  const editWidgetUrl = useMemo(() => {
    return buildSearchParam(SearchParamsKeyNames.View, SearchParamsViewNames.INSIGHTS_EDIT, {
      id: widget.id,
    })
  }, [widget.id])

  return (
    <div
      key={widget.id}
      className={cn(colSpanCss, rowSpanCss, 'max-h-[30rem] overflow-y-auto relative')}
    >
      <Card widget={widget} />

      <div className={cn('absolute top-2 right-2 z-10', widget.type === 'kpi' && 'text-white')}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">{t('openMenu')}</span>
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            {canDownloadCSV && (
              <DropdownMenuItem onClick={handleDownloadCSV}>
                <FileDownIcon className="size-4" />
                <span>{t('downloadCSVButton')}</span>
              </DropdownMenuItem>
            )}
            {canShowBeneficiaries && (
              <DropdownMenuItem onClick={handleShowBeneficiaries}>
                <Users className="size-4" />
                <span>{t('showBeneficiariesButton')}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to={editWidgetUrl}>
                <Pencil className="size-4" />
                Edit widget
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
